/**
 * The live waiting-fee accrual, shared by the orders table's waiting column
 * and the drawer's price cards — a transcription of the old `WaitPriceInfo`
 * math (`single/PriceFormula.tsx`).
 *
 * Rules that are easy to lose:
 * - The fee only accrues at stage 8 (courier waiting at pickup) and ONLY for
 *   equipment class 1 (scooter) — other classes never accrue live.
 * - The clock starts at the arrival of the pickup NOT yet handed over.
 * - `free_wait_time` minutes are free; each FULL minute past that costs
 *   `price_per_minute_after_wait`, falling back to 500 so'm when the rate is
 *   absent or negative (the old panel's hardcoded default).
 */

export interface LegacyWaitFeeInput {
  stage: number;
  equipmentId: number | null;
  freeWaitTime: number;
  pricePerMinuteAfterWait: number | null;
  /** Pickup legs — only `arrived_at`/`handed_over_at` are read. */
  origin: { arrived_at: string | null; handed_over_at: string | null }[];
}

/** The pickup the courier is currently waiting at, or null. */
export function legacyPendingArrivedAt(
  origin: LegacyWaitFeeInput['origin'],
): string | null {
  return origin.find((leg) => !leg.handed_over_at)?.arrived_at ?? null;
}

/** The per-minute rate with the old panel's 500 so'm fallback applied. */
export function legacyWaitRate(rate: number | null): number {
  return typeof rate === 'number' && rate >= 0 ? rate : 500;
}

/**
 * The accrued extra fee, in so'm, at `now`. Returns 0 outside stage 8 /
 * non-scooter / not-yet-arrived, exactly as the source did.
 */
export function legacyExtraWaitPrice(input: LegacyWaitFeeInput, now: Date): number {
  if (input.stage !== 8 || input.equipmentId !== 1) return 0;
  const arrivedAt = legacyPendingArrivedAt(input.origin);
  if (!arrivedAt) return 0;
  const arrived = new Date(arrivedAt);
  if (Number.isNaN(arrived.getTime())) return 0;
  const freeSeconds = Math.max(input.freeWaitTime, 0) * 60;
  const diffSec = Math.floor((now.getTime() - arrived.getTime()) / 1000);
  if (diffSec <= freeSeconds) return 0;
  const fullMinutes = Math.floor((diffSec - freeSeconds) / 60);
  return fullMinutes * legacyWaitRate(input.pricePerMinuteAfterWait);
}
